import type { SvgType } from '@wix/document-services-types'

const SVG_TYPES = {
	SHAPE: 'shape',
	TINT: 'tint',
	COLOR: 'color',
	UGC: 'ugc',
} as const

type SvgInfoColors = {
	color1?: string
	color2?: string
	color3?: string
	color4?: string
	color5?: string
	color6?: string
	color7?: string
	color8?: string
}

export type SvgInfo = {
	svgType: SvgType
	viewBox: string
	bbox: string
} & SvgInfoColors

type SVGDimensions = { width: number | string; height: number | string }

export type SvgProperties = {
	preserveViewBox?: string
	displayMode?: string
	aspectRatio?: string
	viewBoxOverride?: string
}

const HAIRLINE_FIX_FACTOR = 1

function getViewBoxFromData(svgInfo: Partial<SvgInfo>, properties: SvgProperties = {}) {
	const { svgType, viewBox, bbox } = svgInfo
	const { preserveViewBox } = properties

	if (svgType !== SVG_TYPES.UGC && !preserveViewBox && bbox) {
		return bbox
	}
	return viewBox
}

/**
 * Returns a data URI SVG encoded for CSS <url> value
 */
function encodeSVG(svg: string) {
	const escaped = svg
		.replace(/"/g, "'") // Use single quotes instead of double to avoid encoding.
		.replace(/>\s+</g, '><') // trim
		.replace(/\s{2,}/g, ' ') // trim
		.replace(symbols, encodeURIComponent) // encode symbols

	return `url("data:image/svg+xml,${escaped}")`
}

const viewBoxMatcher = /(viewBox=")([^"]*)(")/i
const preserveAspectRatioMatcher = /(preserveAspectRatio=")([^"]*)(")/i
const svgWidthMatcher = /(?:<svg[^>]*)\s(width="[^"]*")/i
const svgHeightMatcher = /(?:<svg[^>]*)\s(height="[^"]*")/i
const svgTagMatcher = /(<svg[^>]*)(>)/
const symbols = /[\r\n%#()<>?[\\\]^`{|}]/g

function getScaledSvgViewBox(svgString: string, svgInfo: Partial<SvgInfo>, properties: SvgProperties = {}) {
	if (svgString) {
		const { svgType, viewBox, bbox } = svgInfo
		const { preserveViewBox, displayMode, aspectRatio } = properties

		const preserveAspectRatio = aspectRatio || (displayMode === 'stretch' ? 'none' : 'xMidYMid meet')

		let svgTag = svgString.match(svgTagMatcher)?.[0]

		if (svgTag) {
			if (preserveAspectRatioMatcher.test(svgTag)) {
				svgString = svgString.replace(
					svgTag,
					svgTag.replace(preserveAspectRatioMatcher, `$1${preserveAspectRatio}$3`)
				)
			} else {
				svgString = svgString.replace(/<svg/, `<svg preserveAspectRatio="${preserveAspectRatio}"`)
			}
		} else {
			// no top svg tag so bail out
			return svgString
		}

		if (!viewBoxMatcher.test(svgTag)) {
			if (svgType === SVG_TYPES.UGC && viewBox) {
				svgString = svgString.replace(/<svg/, `<svg viewBox="${viewBox}"`)
			}
			if (svgType !== SVG_TYPES.UGC && bbox) {
				svgString = svgString.replace(/<svg/, `<svg viewBox="${bbox}"`)
			}
		}

		if (svgType !== SVG_TYPES.UGC && !preserveViewBox && bbox) {
			svgTag = svgString.match(svgTagMatcher)![0]
			svgString = svgString.replace(svgTag, svgTag.replace(viewBoxMatcher, `$1${bbox}$3`))
		}
	}

	return svgString
}

function trimSvgContents(svgContent: string) {
	const svgContentStartRe = /^(.*\s*)<svg/
	const svgContentEndRe = /<\/svg>(\s*)$/

	return svgContent.replace(svgContentStartRe, '<svg').replace(svgContentEndRe, '</svg>')
}

function removeWidthAndHeight(svgString: string, addIfMissing?: boolean | SVGDimensions) {
	if (svgString) {
		const widthMatch = svgString.match(svgWidthMatcher)
		const heightMatch = svgString.match(svgHeightMatcher)
		if (widthMatch && widthMatch.length > 1) {
			svgString = svgString.replace(widthMatch[1], 'width="100%"')
		}
		if (heightMatch && heightMatch.length > 1) {
			svgString = svgString.replace(heightMatch[1], 'height="100%"')
		}

		if (addIfMissing && !(widthMatch && heightMatch)) {
			const dimensions = addIfMissing as SVGDimensions
			const newWidth = dimensions.width ? ` width="${dimensions.width}"` : ' width="100%"'
			const newHeight = dimensions.height ? ` height="${dimensions.height}"` : ' height="100%"'
			svgString = svgString.replace(
				svgTagMatcher,
				`$1${!widthMatch ? newWidth : ''}${!heightMatch ? newHeight : ''}$2`
			)
		}
	}
	return svgString
}

function addSvgStyleNode(svgString: string, defsValue: string) {
	if (svgString && defsValue) {
		return svgString.replace(/(<svg[^>]*>)/, `$1<defs><style>${defsValue}</style></defs>`)
	}

	return svgString
}

function setSvgId(svgString: string, svgId: string) {
	if (!svgString) {
		return svgString
	}

	const svgTagMatch = svgString.match(svgTagMatcher)

	if (svgTagMatch) {
		let svgTag = svgTagMatch[0]
		const idMatch = svgTag.match(/id="[-\w]+"/)

		if (idMatch) {
			svgTag = svgTag.replace(idMatch[0], `id="${svgId}"`)
		} else {
			svgTag = svgTag.replace('<svg ', `<svg id="${svgId}" `)
		}

		svgString = svgString.replace(svgTagMatch[0], svgTag)
	}

	return svgString
}

function invertSvgContent(svgContent: string, x: number, y: number, width: number, height: number) {
	const pathDRe = /<path.*\s*d="([^"]+)"/
	const pathTagRe = /(<path)[^>]*>/

	const pathTag = svgContent.match(pathTagRe)?.[1] || ''
	const pathData = svgContent.match(pathDRe)?.[1] || ''

	const rectPathSuffix = `M${x},${y - HAIRLINE_FIX_FACTOR} h${width} v${height + HAIRLINE_FIX_FACTOR} h${-width} Z`
	return svgContent
		.replace(pathData, `${pathData} ${rectPathSuffix}`)
		.replace(pathTag, `${pathTag} fill-rule="evenodd"`)
}

const getBackgroundProps = ({
	compId,
	svg,
	side,
	height = '150px',
	color = '#000',
	invert = false,
	keepAspectRatio = true,
	repeats = 0,
}: {
	compId: string
	svg: any
	side: 'top' | 'bottom'
	height: string
	color: string
	invert: boolean
	keepAspectRatio: boolean
	repeats: number
}) => {
	const aspectRatio = keepAspectRatio ? (repeats !== 0 ? 'xMinYMax meet' : 'xMidYMax slice') : 'none'

	const { info, content } = svg

	const viewBox = getViewBoxFromData(info)

	if (!content || !viewBox) {
		return {
			image: content ? encodeSVG(content) : '',
		}
	}

	const [vbX, vbY, vbWidth, vbHeight] = viewBox.split(' ').map((n) => parseFloat(n))
	const viewBoxOverride = `${vbX} ${vbY} ${vbWidth} ${vbHeight}`

	const scaledSvg = getScaledSvgViewBox(
		content,
		{
			svgType: info.svgType,
			bbox: viewBoxOverride,
			viewBox: viewBoxOverride,
		},
		{ aspectRatio }
	)
	const uniqueId = `${compId}-${side}`

	const trimmedContent = trimSvgContents(scaledSvg)
	const processedSvgContent = invert ? invertSvgContent(trimmedContent, vbX, vbY, vbWidth, vbHeight) : trimmedContent
	const invertingTransform = invert
		? `#${uniqueId} > g { transform: scaleY(-1) translateY(${-(2 * vbY + vbHeight)}px); } `
		: ''
	const pathColoring = info.svgType === 'color' && !info.color2 ? `, #${uniqueId} [data-color="1"] ` : ''
	const svgStyle = `${invertingTransform}#${uniqueId}${pathColoring} { fill: ${color}; }`

	const image = encodeSVG(
		removeWidthAndHeight(addSvgStyleNode(setSvgId(processedSvgContent, uniqueId), svgStyle), {
			width: vbWidth.toFixed(2),
			height: vbHeight.toFixed(2),
		})
	)

	const size = `${
		// !NOTE: assuming height is in px!
		// When in aspect-ratio we're calculating width instead of `auto`
		// because of rendering bugs in Safari (vertical hairlines
		// between horizontally repeated SVG as background-image).
		// Since we have fixed height - use it instead of 100%
		// Also limit fraction digits to 2 repeat in stretch - seems to do better in Safari - same issue
		keepAspectRatio
			? `${Math.ceil((vbWidth * parseInt(height, 10)) / vbHeight)}px`
			: `${((1 / (repeats + 1)) * 100).toFixed(2)}%`
	} 100%`
	return { backgroundImage: image, backgroundSize: size }
}

export { getBackgroundProps }
